import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Quote, Sparkles, ShieldAlert, Link2, Search, Radio, Newspaper } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n, formatRelative, formatExact } from "@/i18n";
import { ShareButton } from "@/components/ShareCard";

const CATS = ["latest", "tech_leaders", "official_company", "ai_research", "ai_products"];

function PulseCard({ p }) {
  const { t, lang } = useI18n();
  const headline = (lang === "ar" && p.headline_ar) || p.ai_headline;
  const summary = (lang === "ar" && p.summary_ar) || p.ai_summary;
  return (
    <article data-testid="x-pulse-card" className="fade-up relative rounded-2xl bg-[#0d0f16] text-slate-100 ring-1 ring-white/10 hover:ring-violet-500/50 overflow-hidden flex flex-col transition-shadow hover:shadow-[0_10px_40px_rgba(124,58,237,0.25)]">
      <span className="absolute top-0 inset-x-0 h-0.5 bg-gradient-to-r from-violet-500 via-cyan-400 to-violet-500" />
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center justify-between gap-2">
          <span data-testid="x-pulse-label" className="inline-flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-wider bg-white text-black px-2 py-0.5 rounded">
            <span className="font-black">𝕏</span> {t("xp.direct")}
          </span>
          <span className="text-[10px] font-mono uppercase tracking-wider text-violet-300">{t(`xp.${p.category}`)}</span>
        </div>
        <div className="mt-4 flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-600 to-cyan-500 inline-flex items-center justify-center font-bold text-sm">{p.author_name?.[0]}</span>
          <div className="min-w-0">
            <div data-testid="x-pulse-author" className="text-sm font-semibold truncate">{p.author_name}</div>
            <div className="text-xs text-slate-400 truncate" dir="ltr">@{p.handle}</div>
          </div>
        </div>
        <h3 data-testid="x-pulse-headline" className="mt-4 font-display font-bold text-[17px] leading-snug">
          <Quote size={14} className="inline -mt-1 me-1 text-violet-400" />{headline}
        </h3>
        {summary && <p className="mt-2 text-[13.5px] text-slate-300 leading-relaxed">{summary}</p>}
        <div className="mt-3 flex flex-wrap gap-1.5 text-[10px]">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-200"><Sparkles size={10} /> {t("xp.aiNote")}</span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-200"><ShieldAlert size={10} /> {t("xp.unverified")}</span>
        </div>
        {p.related_articles?.length > 0 && (
          <div className="mt-3 rounded-lg bg-white/5 p-2.5">
            <div className="text-[10px] font-mono uppercase tracking-wider text-cyan-300 mb-1 inline-flex items-center gap-1"><Newspaper size={11} /> {t("xp.related")}</div>
            {p.related_articles.map((r) => <Link key={r.id} to={`/article/${r.id}`} className="block text-xs text-slate-300 hover:text-white truncate">· {r.title} — {r.source_name}</Link>)}
          </div>
        )}
        <div className="mt-auto pt-4 flex items-center justify-between gap-2 text-xs">
          <span className="text-slate-400" title={formatExact(p.created_at, lang)}>{t("xp.posted")} {formatRelative(p.created_at, lang)}</span>
          <ShareButton kind="x" article={{ id: p.id, title: p.ai_headline, title_ar: p.headline_ar, source_name: p.author_name }} />
          <a data-testid="x-pulse-original" href={p.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-violet-300 hover:text-white"><Link2 size={12} /> {t("xp.viewPost")}</a>
        </div>
      </div>
    </article>
  );
}

export default function XPulse() {
  const { t } = useI18n();
  const [f, setF] = useState({ category: "latest", account: "", q: "", date_from: "", date_to: "" });
  const params = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();
  const { data, isLoading } = useQuery({ queryKey: ["x-pulse", params], queryFn: async () => (await api.get(`/x-pulse?limit=40&${params}`)).data, refetchInterval: 120000 });
  const { data: accounts } = useQuery({ queryKey: ["x-accounts"], queryFn: async () => (await api.get("/x-pulse/accounts")).data });
  const { data: status } = useQuery({ queryKey: ["x-status"], queryFn: async () => (await api.get("/x-pulse/status")).data });
  const inp = "px-3 py-2 text-sm bg-slate-100 border border-slate-200 rounded-full outline-none focus:border-violet-400";

  return (
    <div className="pt-16">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-4">
        <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-violet-700">
          <Radio size={14} className={status?.live ? "radar-spin" : ""} /> {status?.live ? t("xp.syncInfo").replace("{n}", status.interval_minutes) : t("xp.notReal")}
        </div>
        <h1 data-testid="x-pulse-title" className="mt-2 font-display font-extrabold text-4xl sm:text-5xl tracking-tight">{t("xp.title")}</h1>
        <p className="mt-3 text-slate-600 max-w-3xl leading-relaxed">{t("xp.desc")}</p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-2">
          {CATS.map((c) => (
            <button key={c} data-testid={`x-pulse-cat-${c}`} onClick={() => setF({ ...f, category: c })} className={`whitespace-nowrap px-3.5 py-1.5 rounded-full text-[13px] font-medium transition-colors ${f.category === c ? "bg-black text-white" : "bg-slate-100 text-slate-600 hover:bg-violet-50"}`}>{t(`xp.${c === "latest" ? "latest" : c}`)}</button>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <div className="relative"><Search size={14} className="absolute start-3 top-1/2 -translate-y-1/2 text-slate-400" /><input data-testid="x-pulse-search" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder={t("xp.search")} className={`${inp} ps-8`} /></div>
          <select data-testid="x-pulse-account" value={f.account} onChange={(e) => setF({ ...f, account: e.target.value })} className={inp}>
            <option value="">{t("xp.allAccounts")}</option>
            {accounts?.map((a) => <option key={a.handle} value={a.handle}>{`${a.display_name} (@${a.handle})`}</option>)}
          </select>
          <label className="text-xs text-slate-500 inline-flex items-center gap-1">{t("xp.from")} <input data-testid="x-pulse-from" type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className={inp} /></label>
          <label className="text-xs text-slate-500 inline-flex items-center gap-1">{t("xp.to")} <input data-testid="x-pulse-to" type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className={inp} /></label>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">{[0, 1, 2].map((i) => <div key={i} className="h-64 rounded-2xl bg-slate-200 animate-pulse" />)}</div>
        ) : data?.items?.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">{data.items.map((p) => <PulseCard key={p.id} p={p} />)}</div>
        ) : (
          <div data-testid="x-pulse-empty" className="rounded-2xl bg-[#0d0f16] text-slate-200 p-10 text-center ring-1 ring-white/10">
            <span className="font-black text-4xl">𝕏</span>
            <h2 className="mt-3 font-display font-bold text-lg">{t("xp.emptyTitle")}</h2>
            <p className="mt-2 text-sm text-slate-400 max-w-lg mx-auto">{t("xp.emptyDesc")}</p>
          </div>
        )}
      </section>
    </div>
  );
}
